/**
 * BL-2338 — the request body of `POST /api/lots/:id/media`: one photo or video,
 * named by the id the caller minted (the offline queue's idempotency key).
 *
 * Built from the persisted record's own field rules (`inventoryMediaItemFieldsSchema`)
 * so the per-item door and the lot PATCH's `mediaChanges` add (BL-2348) cannot
 * disagree on what a valid record is. Two differences, both because this door decides placement itself:
 * `sortOrder` and `isCover` are accepted and ignored (an added item goes last and
 * never takes the cover), and `storagePath` is required (it is the only locator
 * since BL-1477, so a record without one would be dropped on read).
 */
import { z } from '@auctogon/core/zod';
import {
  inventoryMediaItemFieldsSchema,
  MAX_DOCUMENT_ID_LENGTH,
  MAX_DOCUMENT_NAME_LENGTH,
  MAX_EXTERNAL_DOCS_PER_LOT,
  MAX_MEDIA_PER_LOT,
  toPersistedMediaFields,
} from '@auctogon/core/inventory';
import { storagePathSchema } from '@auctogon/core/validation/storage-path';

/** A media id as the change lists and the order carry it (the id the caller minted). */
export const mediaIdSchema = z.string().min(1).max(200);

/** The add-one-media body. Its output is the persisted record minus placement. */
export const LotMediaAddRequestSchema = inventoryMediaItemFieldsSchema
  .extend({
    id: z.string().min(1, 'Media id is required').max(200),
    storagePath: storagePathSchema('Media storage path'),
    sortOrder: z.number().int().nonnegative().optional(),
    isCover: z.boolean().optional(),
  })
  // Same write-side strip as the persisted schema (BL-1455, BL-2439): `url` and
  // `thumbnail` are checked, then dropped, and a photo's poster path goes.
  // Placement is the server's, so it goes too.
  .transform(({ sortOrder: _sortOrder, isCover: _isCover, ...fields }) => toPersistedMediaFields(fields));

/** The parsed add-one-media body. */
export type LotMediaAddRequest = z.output<typeof LotMediaAddRequestSchema>;

/**
 * BL-2348 — the lot drawer's per-item media change list, carried on the lot
 * PATCH instead of a whole `media` array: records to add (each the per-item add
 * body above), ids to remove, and optionally the ids in their new order (Main
 * is the first). Applied under the lot lock by BL-2338's per-item edits.
 */
export const LotMediaChangesSchema = z
  .object({
    add: z.array(LotMediaAddRequestSchema).max(MAX_MEDIA_PER_LOT),
    remove: z.array(mediaIdSchema).max(MAX_MEDIA_PER_LOT),
    order: z.array(mediaIdSchema).max(MAX_MEDIA_PER_LOT).optional(),
  })
  .strict();

/**
 * BL-2417 — the request body of `PUT /api/lots/:id/media/order`: the lot's photo
 * ids in their new order (the first becomes Main), so the offline queue can
 * replay a reorder or "Set as main" as one op. An id the lot no longer has is
 * ignored and an unnamed one keeps its place (`applyLotMediaOrder`).
 *
 * `baseOrder` is the order the caller saw when the edit began. When another
 * device has reordered since, the route answers the lot save's 409
 * `STALE_FIELDS`. `overwrite: true` drops the check.
 */
export const LotMediaOrderRequestSchema = z
  .object({
    ids: z.array(mediaIdSchema).min(1).max(MAX_MEDIA_PER_LOT),
    baseOrder: z.array(mediaIdSchema).max(MAX_MEDIA_PER_LOT).optional(),
    overwrite: z.boolean().optional(),
  })
  .strict();

/**
 * BUG-2061 — the request body of `POST /api/lots/:id/documents`, and each add in
 * the PATCH's document change list: one document, named by the id the caller
 * minted. Plain `z.object`, so unknown keys (the round-tripped `url`, BL-1477)
 * are stripped. The storage path's tenant is the route's check
 * (`validateExternalDocuments`), which needs the gate's tenant.
 */
export const LotDocumentAddRequestSchema = z.object({
  id: z
    .string()
    .min(1, 'Document id is required')
    .max(MAX_DOCUMENT_ID_LENGTH, `Document id must be ${MAX_DOCUMENT_ID_LENGTH} characters or fewer`),
  name: z
    .string()
    .min(1, 'Document name is required')
    .max(MAX_DOCUMENT_NAME_LENGTH, `Document name must be ${MAX_DOCUMENT_NAME_LENGTH} characters or fewer`),
  storagePath: storagePathSchema('Document storage path'),
  type: z.enum(['pdf', 'image', 'other']),
  uploadedAt: z.string().refine((value) => !Number.isNaN(Date.parse(value)), 'uploadedAt must be a valid ISO timestamp'),
  // BL-961: the auction→lot fan-out flag; absent stays absent.
  appliesToAllLots: z.boolean().optional(),
});

/**
 * BL-2348 — the lot drawer's per-item document change list. Each add is the
 * per-item add's own body (BUG-2061); the route then runs the per-item add's
 * validator (`validateExternalDocuments`) for the storage path's tenant.
 */
export const LotDocumentChangesSchema = z
  .object({
    add: z.array(LotDocumentAddRequestSchema).max(MAX_EXTERNAL_DOCS_PER_LOT),
    remove: z.array(z.string().min(1).max(MAX_DOCUMENT_ID_LENGTH)).max(MAX_EXTERNAL_DOCS_PER_LOT),
  })
  .strict();

/**
 * BL-2676 (ADR 070 — Ingest Protocol § 4) — the byte limits of the media ingest route, shared with the
 * phone's upload lane so both read one number. The route refuses a body over `totalBytes` before reading
 * it; a photo larger than `nativeThresholdBytes` takes the video chain (signed link, PUT, attach).
 */
export const LOT_MEDIA_INGEST_LIMITS = {
  totalBytes: 12 * 1024 * 1024,
  photoBytes: 10 * 1024 * 1024,
  nativeThresholdBytes: 8 * 1024 * 1024,
  thumbnailBytes: 102_400,
} as const;

/** A client media id as the ingest route takes it in the path: a lowercase UUID v4 (ADR 070 § 1). */
export const LOT_MEDIA_INGEST_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;

/**
 * The `meta` part of `PUT /api/lots/:id/media/:mediaId`: where the item goes and what it is.
 * A photo sends `contentType`; a video attach (no `photo` part) sends `type: 'video'` and the
 * `storagePath` of the file it already put at its signed link.
 */
export const LotMediaIngestMetaSchema = z
  .object({
    position: z.number().int().nonnegative().max(MAX_MEDIA_PER_LOT),
    isCover: z.boolean(),
    type: z.enum(['photo', 'video']).default('photo'),
    contentType: z.string().min(1).max(100).optional(),
    storagePath: storagePathSchema('Media storage path').optional(),
    width: z.number().int().positive().optional(),
    height: z.number().int().positive().optional(),
  })
  .strict();

/** The parsed `meta` part. */
export type LotMediaIngestMeta = z.output<typeof LotMediaIngestMetaSchema>;
